import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Calendar
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import userService from '../../services/userService'

const UserMasterForm = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [roles, setRoles] = useState([])
  const [formData, setFormData] = useState({
    userCode: '',
    name: '',
    email: '',
    userEmail: '',
    role: '',
    department: '',
    designation: '',
    phone: '',
    status: 'Active',
    password: '',
    confirmPassword: '',
    createdDate: new Date().toISOString().split('T')[0]
  })

  // Load roles on component mount
  useEffect(() => {
    loadRoles()
  }, [])

  const loadRoles = async () => {
    try {
      // Import roleService to get roles
      const { default: roleService } = await import('../../services/roleService')
      const response = await roleService.getAllRoles()
      if (response.success) {
        setRoles(response.data)
      }
    } catch (error) {
      console.error('Error loading roles:', error)
      // Fallback to hardcoded roles if API fails
      setRoles([
        { _id: '1', roleName: 'Super Admin' },
        { _id: '2', roleName: 'Admin' },
        { _id: '3', roleName: 'Sales Manager' },
        { _id: '4', roleName: 'Sales Admin' },
        { _id: '5', roleName: 'Purchase Manager' },
        { _id: '6', roleName: 'Purchase Admin' }
      ])
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      // Check all required fields
      const requiredFields = [
        { field: 'userCode', label: 'User Code' },
        { field: 'name', label: 'Full Name' },
        { field: 'email', label: 'Email Address' },
        { field: 'role', label: 'Role' },
        { field: 'department', label: 'Department' },
        { field: 'designation', label: 'Designation' },
        { field: 'status', label: 'Status' },
        { field: 'password', label: 'Password' },
        { field: 'confirmPassword', label: 'Confirm Password' }
      ]

      const missingFields = requiredFields.filter(field => !formData[field.field] || formData[field.field].trim() === '')
      
      if (missingFields.length > 0) {
        toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`)
        return
      }

      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }

      const userData = userService.transformUserForSubmission(formData)
      const response = await userService.createUser(userData)
      
      if (response.success) {
        toast.success('User created successfully!', { autoClose: 2000 })
        navigate('/settings/user-master')
      } else {
        toast.error(response.message || 'Failed to create user')
      }
    } catch (error) {
      console.error('Error creating user:', error)
      toast.error('Failed to create user: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      userCode: '',
      name: '',
      email: '',
      userEmail: '',
      role: '',
      department: '',
      designation: '',
      phone: '',
      status: 'Active',
      password: '',
      confirmPassword: '',
      createdDate: new Date().toISOString().split('T')[0]
    })
  }

  const handleBack = () => {
    navigate('/settings/user-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'User Master',
      path: '/settings/user-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>User Master</h1>
          <p>Manage user accounts and permissions</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>User Master</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>User Information</h3>
          </div>
          {/* Row 1: User Code, Full Name, Email Address, Phone Number */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="userCode" className="form-label required">
                User Code
              </label>
              <input
                type="text"
                id="userCode"
                name="userCode"
                value={formData.userCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter user code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="name" className="form-label required">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label required">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter email address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          <div className="section-header">
            <h3>Role & Organization</h3>
          </div>
          {/* Row 2: Role, Department, Designation, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="role" className="form-label required">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.roleName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="department" className="form-label required">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter department"
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation" className="form-label required">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter designation"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="section-header">
            <h3>Account Security</h3>
          </div>
          {/* Row 3: Password, Confirm Password, Created Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="password" className="form-label required">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label required">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Confirm password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="createdDate" className="form-label required">
                Created Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  id="createdDate"
                  name="createdDate"
                  value={formData.createdDate}
                  onChange={handleInputChange}
                  className="form-input date-input"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserMasterForm